import { Injectable } from '@angular/core';
import { STATUS_NAMES_EXCEPT_CANCELED } from '@core/consts';
import {
  DeadlinesTimelineColumnsByOptions,
  DeadlinesTimelineItem,
  DeadlinesTimelineItemSerie,
  DeadlinesTimelineOptions,
  DeadlinesWhere,
  MaintenancesQueryParams
} from '@core/interfaces';
import { clearEmptyProperties } from '@core/utils/filters-utils';
import { TranslateService } from '@ngx-translate/core';
import { chain, clone } from 'lodash';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class DeadlinesMapperService {
  public readonly timelineOptions: DeadlinesTimelineOptions = {
    lastSevenDays: moment().subtract(6, 'days').startOf('day').utc().toISOString(),
    lastThirtyDays: moment().subtract(29, 'days').startOf('day').utc().toISOString(),
    lastNinetyDays: moment().subtract(89, 'days').startOf('day').utc().toISOString(),
    lastSixMonths: moment().subtract(5, 'months').startOf('day').utc().toISOString(),
    lastTwelveMonths: moment().subtract(11, 'months').startOf('day').utc().toISOString(),
  };

  public readonly columnsByTimelineOption: DeadlinesTimelineColumnsByOptions = {
    lastSevenDays: 7,
    lastThirtyDays: 10,
    lastNinetyDays: 9,
    lastSixMonths: 6,
    lastTwelveMonths: 12,
  };

  constructor(private translateService: TranslateService) {
    moment.locale(this.translateService.currentLang || 'pt');
  }

  public toMaintenancesQueryParams ({ where, formValue }): MaintenancesQueryParams {
    const query = clone(where);
    const { initialDate, finalDate, dateFilterType } = formValue;

    const mappedQuery = {
      status_in: STATUS_NAMES_EXCEPT_CANCELED.join(','),
      locationGroupId_in: query.locationGroupId_in && query.locationGroupId_in.join(','),
      locationId_in: query.locationId_in && query.locationId_in,
      typeId_in: query.maintenanceTypeId_in && query.maintenanceTypeId_in.join(','),
      segmentId_in: query.segmentId_in && query.segmentId_in.join(','),
      companyId_in: query.companyId_in && query.companyId_in.join(',')
    };

    mappedQuery[`${dateFilterType}_gte`] = moment(initialDate).startOf('day').format('YYYY-MM-DD');
    mappedQuery[`${dateFilterType}_lte`] = moment(finalDate).endOf('day').format('YYYY-MM-DD');

    return clearEmptyProperties(mappedQuery) as MaintenancesQueryParams;
  }

  public toTimelineWhere({ where, timelineOption }): DeadlinesWhere {
    const whereInput = clone(where);
    delete whereInput.closedAt_gte;
    delete whereInput.closedAt_lte;
    delete whereInput.scheduledAt_gte;
    delete whereInput.scheduledAt_lte;

    whereInput.openedAt_lte = moment().endOf('day').utc().toISOString();
    whereInput.openedAt_gte = this.timelineOptions[timelineOption];

    return whereInput;
  }

  public toTimeline({ type, data, timelineOption }): DeadlinesTimelineItem[] {
    if (timelineOption === 'lastSevenDays') {
      return this.toTimelineLastSevenDays({ type, data });
    }

    if (timelineOption === 'lastThirtyDays' || timelineOption === 'lastNinetyDays') {
      const range = timelineOption === 'lastThirtyDays' ? 3 : 10;
      return this.toTimelineLastWithRangedDays({ type, data, range, timelineOption });
    }

    return this.toTimelineLastMonths({ type, data, timelineOption });
  }

  private toTimelineLastSevenDays({ type, data }): DeadlinesTimelineItem[] {
    const timeline = [];

    for (let i = 0; i < this.columnsByTimelineOption.lastSevenDays; i++) {
      const date = moment().subtract(i, 'days');
      const formattedDate = date.format('YYYY-MM-DD');

      const items = data.filter(({ day }) => moment(day).isSame(formattedDate));
      const series = this.toTimelineResponseSeries({ type, items });

      timeline.push({
        name: date.format('ddd DD/MM'),
        series,
      });
    }

    return timeline.reverse();
  }

  private toTimelineLastWithRangedDays({ type, data, range, timelineOption }): DeadlinesTimelineItem[] {
    const timeline = [];
    const firstDayOfSheet = this.timelineOptions[timelineOption];

    for (let i = 0; i < this.columnsByTimelineOption[timelineOption]; i++) {
      const firstDay = moment(firstDayOfSheet).add(i * range, 'days').format('YYYY-MM-DD');
      const lastDay = moment(firstDay).add(range - 1, 'days').format('YYYY-MM-DD');

      const items = data.filter(({ day }) => {
        return moment(day).isSameOrAfter(firstDay) && moment(day).isSameOrBefore(lastDay);
      });

      const series = this.toTimelineResponseSeries({ type, items });

      timeline.push({
        name: `${moment(firstDay).format('DD/MM')} - ${moment(lastDay).format('DD/MM')}`,
        series,
      });
    }

    return timeline;
  }

  private toTimelineLastMonths({ type, data, timelineOption }): DeadlinesTimelineItem[] {
    const firstDayOfSheet = this.timelineOptions[timelineOption];
    const timeline = [];

    for (let i = 0; i < this.columnsByTimelineOption[timelineOption]; i++) {
      const date = moment(firstDayOfSheet).add(i, 'months').format('YYYY-MM-DD');
      const items = data.filter(({ day }) => moment(day).isSame(date, 'month'));
      const series = this.toTimelineResponseSeries({ type, items });

      timeline.push({
        name: moment(date).format('MMM YYYY'),
        series,
      });
    }

    return timeline;
  }

  private toTimelineResponseSeries({ type, items }): DeadlinesTimelineItemSerie[] {
    const onPeriodKey = `${type}InPeriodCount`;
    const outPeriodKey = `${type}OutPeriodCount`;

    const onPeriodCount = chain(items)
      .map((item) => item[onPeriodKey])
      .sum()
      .value();

    const outPeriodCount = chain(items)
      .map((item) => item[outPeriodKey])
      .sum()
      .value();

    return [
      {
        name: outPeriodKey,
        value: outPeriodCount,
        onPeriodCount,
        outPeriodCount,
      },
      {
        name: onPeriodKey,
        value: onPeriodCount,
        onPeriodCount,
        outPeriodCount,
      }
    ];
  }
}
